import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { analyticsService } from '@/services/analytics.service';
import type {
  ClientActivityQuery,
  CriticalAlertsQuery,
  PendingActionsQuery,
  ScheduledOperationsQuery,
  ScheduledTransactionsQuery,
  TransactionsQuery,
} from '@/types/analytics';

/**
 * Analytics Hooks
 * React Query hooks for /api/v1/analytics/* endpoints
 */

// Query keys
export const analyticsKeys = {
  all: ['analytics'] as const,
  employeeAnalytics: () => [...analyticsKeys.all, 'employees'] as const,
  organizationInsights: () => [...analyticsKeys.all, 'insights'] as const,
  // Super Admin Dashboard
  superAdminDashboard: () => [...analyticsKeys.all, 'superAdmin', 'dashboard'] as const,
  clientActivity: (query?: ClientActivityQuery) =>
    [...analyticsKeys.all, 'superAdmin', 'clientActivity', query] as const,
  scheduledOperations: (query?: ScheduledOperationsQuery) =>
    [...analyticsKeys.all, 'superAdmin', 'scheduledOperations', query] as const,
  criticalAlerts: (query?: CriticalAlertsQuery) =>
    [...analyticsKeys.all, 'superAdmin', 'criticalAlerts', query] as const,
  // Admin Dashboard
  adminDashboard: () => [...analyticsKeys.all, 'admin', 'dashboard'] as const,
  transactions: (query?: TransactionsQuery) =>
    [...analyticsKeys.all, 'admin', 'transactions', query] as const,
  pendingActions: (query?: PendingActionsQuery) =>
    [...analyticsKeys.all, 'admin', 'pendingActions', query] as const,
};

/**
 * Hook to fetch employee analytics (list of employees with stats)
 */
export const useEmployeeAnalytics = () => {
  return useQuery({
    queryKey: analyticsKeys.employeeAnalytics(),
    queryFn: () => analyticsService.getEmployeeAnalytics(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to fetch organization insights (metrics for dashboard cards)
 */
export const useOrganizationInsights = () => {
  return useQuery({
    queryKey: analyticsKeys.organizationInsights(),
    queryFn: () => analyticsService.getOrganizationInsights(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// ===== SUPER ADMIN DASHBOARD HOOKS =====

/**
 * Hook to get main super admin dashboard
 */
export const useSuperAdminDashboard = () => {
  return useQuery({
    queryKey: analyticsKeys.superAdminDashboard(),
    queryFn: () => analyticsService.getSuperAdminDashboard(),
    staleTime: 2 * 60 * 1000, // 2 minutes (real-time data needs freshness)
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get full client activity list (paginated)
 */
export const useClientActivity = (query?: ClientActivityQuery) => {
  return useQuery({
    queryKey: analyticsKeys.clientActivity(query),
    queryFn: () => analyticsService.getClientActivity(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get scheduled operations list (paginated)
 */
export const useScheduledOperations = (query?: ScheduledOperationsQuery) => {
  return useQuery({
    queryKey: analyticsKeys.scheduledOperations(query),
    queryFn: () => analyticsService.getScheduledOperations(query),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to get critical alerts list (paginated)
 */
export const useCriticalAlerts = (query?: CriticalAlertsQuery) => {
  return useQuery({
    queryKey: analyticsKeys.criticalAlerts(query),
    queryFn: () => analyticsService.getCriticalAlerts(query),
    staleTime: 1 * 60 * 1000, // 1 minute (critical alerts need frequent updates)
  });
};

// ===== ADMIN DASHBOARD HOOKS =====

/**
 * Hook to get main admin dashboard
 */
export const useAdminDashboard = () => {
  return useQuery({
    queryKey: analyticsKeys.adminDashboard(),
    queryFn: () => analyticsService.getAdminDashboard(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};

/**
 * Hook to get full transactions history (paginated)
 */
export const useAdminTransactions = (query?: TransactionsQuery) => {
  return useQuery({
    queryKey: analyticsKeys.transactions(query),
    queryFn: () => analyticsService.getTransactions(query),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

/**
 * Hook to get full pending actions list (paginated)
 */
export const useAdminPendingActions = (query?: PendingActionsQuery) => {
  return useQuery({
    queryKey: analyticsKeys.pendingActions(query),
    queryFn: () => analyticsService.getPendingActions(query),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

// ===== OPERATIONS DASHBOARD HOOKS =====

/**
 * Hook to fetch operations metrics
 */
export const useOperationsMetrics = () => {
  return useQuery({
    queryKey: ['operations', 'metrics'] as const,
    queryFn: () => analyticsService.getOperationsMetrics(),
    staleTime: 1 * 60 * 1000, // 1 minute
  });
};

/**
 * Hook to fetch scheduled transactions
 */
export const useScheduledTransactions = (query?: ScheduledTransactionsQuery) => {
  return useQuery({
    queryKey: ['operations', 'scheduled', query] as const,
    queryFn: () => analyticsService.getScheduledTransactions(query),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};

/**
 * Hook to cancel a scheduled transaction
 */
export const useCancelScheduledTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => analyticsService.cancelScheduledTransaction(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['operations', 'scheduled'] });
      void queryClient.invalidateQueries({ queryKey: ['operations', 'metrics'] });
    },
  });
};

/**
 * Hook to retry a failed transaction
 */
export const useRetryTransaction = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => analyticsService.retryTransaction(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['operations', 'scheduled'] });
      void queryClient.invalidateQueries({ queryKey: ['operations', 'metrics'] });
    },
  });
};
